import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiService, Lead } from '../../services/apiService'

interface LeadState {
  currentLead: Lead | null
  leads: Lead[]
  loading: boolean
  error: string | null
  nextLeadAvailable: boolean
}

const initialState: LeadState = {
  currentLead: null,
  leads: [],
  loading: false,
  error: null,
  nextLeadAvailable: false,
}

export const fetchCurrentLead = createAsyncThunk(
  'leads/fetchCurrent',
  async () => {
    // This will need to be implemented in apiService
    const response = await apiService.getCurrentLead()
    return response
  }
)

export const fetchLeadById = createAsyncThunk(
  'leads/fetchById',
  async (leadId: number) => {
    const response = await apiService.getLead(leadId)
    return response
  }
)

export const getNextLead = createAsyncThunk(
  'leads/getNext',
  async () => {
    const response = await apiService.getNextLead()
    if (response.success && response.lead) {
      return response.lead
    }
    throw new Error(response.message || 'No leads available')
  }
)

export const completeLead = createAsyncThunk(
  'leads/complete',
  async ({ leadId, data }: { leadId: number; data: any }, { rejectWithValue }) => {
    try {
      console.log('🔄 Redux: completeLead thunk started for lead:', leadId);
      const response = await apiService.completeLead(leadId, data)
      console.log('✅ Redux: completeLead thunk successful:', response);
      return response
    } catch (error: any) {
      console.error('❌ Redux: completeLead thunk failed:', error);
      
      // Extract useful error information
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message ||
                          error.message || 
                          'Failed to complete lead'
      
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data
      })
    }
  }
)

export const releaseLead = createAsyncThunk(
  'leads/release',
  async ({ leadId, reason, description }: { leadId: number; reason: string; description: string }, { rejectWithValue }) => {
    try {
      console.log('🔄 Redux: releaseLead thunk started for lead:', leadId);
      const response = await apiService.releaseLead(leadId, { reason, description })
      console.log('✅ Redux: releaseLead thunk successful:', response);
      return response
    } catch (error: any) {
      console.error('❌ Redux: releaseLead thunk failed:', error);
      
      // Extract useful error information
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message ||
                          error.message || 
                          'Failed to release lead'
      
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data
      })
    }
  }
)

const leadSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    updateTimeRemaining: (state, action: PayloadAction<number>) => {
      if (state.currentLead) {
        state.currentLead.time_remaining_seconds = action.payload
      }
    },
    updateTimeElapsed: (state, action: PayloadAction<number>) => {
      if (state.currentLead) {
        state.currentLead.time_elapsed_seconds = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch current lead
      .addCase(fetchCurrentLead.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchCurrentLead.fulfilled, (state, action) => {
        state.loading = false
        state.currentLead = action.payload
        state.error = null
      })
      .addCase(fetchCurrentLead.rejected, (state, action) => {
        state.loading = false
        state.currentLead = null
        state.error = action.error.message || 'Failed to fetch current lead'
      })
      // Fetch lead by ID
      .addCase(fetchLeadById.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchLeadById.fulfilled, (state, action) => {
        state.loading = false
        state.currentLead = action.payload
        state.error = null
      })
      .addCase(fetchLeadById.rejected, (state, action) => {
        state.loading = false
        state.currentLead = null
        state.error = action.error.message || 'Failed to fetch lead'
      })
      // Get next lead
      .addCase(getNextLead.pending, (state) => {
        state.loading = true
      })
      .addCase(getNextLead.fulfilled, (state, action) => {
        state.loading = false
        // The response is just the lead object
        state.currentLead = action.payload
        state.error = null
      })
      .addCase(getNextLead.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'No leads available'
      })
      // Complete lead
      .addCase(completeLead.pending, (state) => {
        state.loading = true
      })
      .addCase(completeLead.fulfilled, (state, action) => {
        console.log('🔄 Redux: completeLead.fulfilled - clearing currentLead')
        state.loading = false
        state.currentLead = null
        state.nextLeadAvailable = (action.payload as any)?.next_lead_available || false
        console.log('✅ Redux: currentLead is now null, component should show success page')
      })
      .addCase(completeLead.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to complete lead'
      })
      // Release lead
      .addCase(releaseLead.fulfilled, (state) => {
        state.currentLead = null
      })
  },
})

export const { updateTimeRemaining, updateTimeElapsed } = leadSlice.actions
export default leadSlice.reducer